import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import type { AdminUser } from '../../types/api/admin-user';

export interface UserSoftDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
  user: AdminUser | null;
}

export const UserSoftDeleteModal: React.FC<UserSoftDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  user,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const validate = (): boolean => {
    if (!reason.trim()) {
      setError('Reason is required');
      return false;
    }
    
    if (reason.trim().length < 5) {
      setError('Reason must be at least 5 characters');
      return false;
    }
    
    if (reason.trim().length > 500) {
      setError('Reason must not exceed 500 characters');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError('Failed to delete user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete User"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Warning Message */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-red-800">
                Warning: This action will soft delete the user
              </h3>
              <p className="text-sm text-red-700 mt-1">
                You are about to delete <strong>{user.name}</strong> ({user.email}).
                The user will be marked as deleted but data will be preserved for audit purposes.
                The user can be restored later if needed.
              </p>
            </div>
          </div>
        </div>

        {/* User Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#EAB308] to-[#CA8A04] flex items-center justify-center text-white font-semibold">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{user.name}</p>
              <p className="text-xs text-gray-600">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Reason Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason for Deletion <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError('');
            }}
            placeholder="Please provide a reason for deleting this user (5-500 characters)"
            rows={4}
            className={`w-full px-3 py-2 border ${
              error ? 'border-red-500' : 'border-gray-300'
            } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 resize-none`}
            disabled={isSubmitting}
          />
          <div className="flex justify-between items-center mt-1">
            {error ? (
              <p className="text-xs text-red-600">{error}</p>
            ) : (
              <p className="text-xs text-gray-500">
                Minimum 5 characters, maximum 500 characters
              </p>
            )}
            <p className={`text-xs ${
              reason.length > 500 ? 'text-red-600' : 'text-gray-500'
            }`}>
              {reason.length}/500
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" disabled={isSubmitting}>
            {isSubmitting ? 'Deleting...' : 'Delete User'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

